import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../../../assets/styles/irprint.css";
import { PRINT_TEXT } from "@utils/constants/print.constants";
import logo from "../../../assets/images/print_logo.png";
import useGetOneGoodsRequest from "@hooks/goods-request/useGetOneGoodsRequest";
import { useGetOneGoodsRequestPrintQuery } from "@states/goods-request/goods-request.api";
import { formatDBDate } from "@utils/general/format-db-date";

const GoodsRequestNotePrintPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data: goodsRequestData,
    isLoading: goodsRequestLoading,
    error: goodsRequestError,
  } = useGetOneGoodsRequestPrintQuery({
    goods_request_id: id!,
  });

  useEffect(() => {
    if (!goodsRequestLoading && goodsRequestData) {
      const timeout = setTimeout(() => {
        window.print();
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [goodsRequestLoading, goodsRequestData]);

  if (goodsRequestLoading)
    return <div className="text-center p-8">Loading...</div>;
  if (goodsRequestError)
    return (
      <div className="text-center p-8 text-red-500">Error loading data</div>
    );

  return (
    <div className="print-page">
      <div className="print-container">
        <div className="flex items-center gap-4 mb-4">
          <div className="logo-container ml-10 mb-5 w-40 h-30">
            <img
              src={logo}
              alt="Company Logo"
              className="w-full h-full object-contain"
            />
          </div>
          {/* Header */}
          <div className="header grn-header text-center mb-0">
            <h1 className="company-name text-xl font-bold">
              {PRINT_TEXT.companyName}
            </h1>
            <h2 className="document-title text-lg font-semibold">
              Goods Requisition Form
            </h2>
          </div>
        </div>

        <div className="border-b border-black flex-1 w-full"></div>

        {/* Document Info */}
        <div>
          <div className="grid grid-cols-12  gap-x-4 gap-y-4 w-full my-4">
            <div className="col-span-6 flex justify-end">
              <span className="label font-semibold min-w-40">
                Request Dept/Branch
              </span>
              <span>:</span>
              <span className="value border-b border-black flex-1">
                {goodsRequestData?.data[0]?.customer.customer_first_name || "-"}
              </span>
            </div>
            <div className="col-span-6 flex justify-end">
              <span className="label font-semibold w-40">
                Receiver/Location
              </span>
              <span>:</span>
              <span className="value border-b border-black flex-1">
                {goodsRequestData?.data[0]?.customer.customer_first_name || ""}
              </span>
            </div>
            <div className="col-span-6 flex justify-end">
              <span className="label font-semibold w-40">Request Purpose</span>
              <span>:</span>
              <span className="value border-b border-black flex-1">
                {goodsRequestData?.data[0]?.description || ""}
              </span>
            </div>
            <div className="col-span-6 flex justify-end">
              <span className="label font-semibold w-40">GRQ NO</span>
              <span>:</span>
              <span className="value border-b border-black flex-1">
                {goodsRequestData?.data[0]?.goods_request_note_code}
              </span>
            </div>
            <div className="col-span-6 flex justify-end">
              <span className="label font-semibold w-40">Date</span>
              <span>:</span>
              <span className="value border-b border-black flex-1">
                {goodsRequestData?.data[0]?.customer.customer_first_name || "-"}
              </span>
            </div>
            <div className="col-span-6 flex justify-end">
              <span className="label font-semibold w-40">Reference No</span>
              <span>:</span>
              <span className="value border-b border-black flex-1">
                {goodsRequestData?.data[0]?.reference_id || "-"}
              </span>
            </div>
            <div className="col-span-6 flex justify-end">
              <span className="label font-semibold w-40">Est.time Arrival</span>
              <span>:</span>
              <span className="value border-b border-black flex-1">
                {goodsRequestData?.data[0]?.delivery_date &&
                  formatDBDate(goodsRequestData?.data[0]?.delivery_date)}
              </span>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <table className="items-table w-full border-collapse mb-0">
          <thead>
            <tr className="bg-gray-100">
              <th className="border p-1">Sr</th>
              <th className="border p-1">Product Code</th>
              <th className="border p-1">Packing</th>
              <th className="border p-1">Unit</th>
              <th className="border p-1">In Hand Qty</th>
              <th className="border p-1">Required Qty</th>
              <th className="border p-1">Issued Qty</th>
              <th className="border p-1">Remark</th>
              {/* <th className="border p-1">{PRINT_TEXT.tableHeaders?.[7]}</th> */}
            </tr>
          </thead>
          <tbody>
            {goodsRequestData?.data[0]?.product_detail?.map(
              (item: any, index: number) => (
                <tr key={index}>
                  <td className="border p-1 text-center">{index + 1}</td>
                  <td className="border p-1">{item?.product_code || ""}</td>
                  <td className="border p-1">{item?.packing || ""}</td>
                  <td className="border p-1">{item?.unit_name || ""}</td>
                  <td className="border p-1 text-center">
                    {item?.available_qty || ""}
                  </td>
                  <td className="border p-1 text-center">
                    {item?.request_qty || "0"}
                  </td>
                  <td className="border p-1 text-center">
                    {item?.issue_qty || "0"}
                  </td>
                  <td className="border p-1 text-center"></td>
                  {/* <td className="border p-1">{item?.remark || '-'}</td> */}
                </tr>
              )
            )}
          </tbody>
        </table>

        <div className="flex justify-between mt-10 space-x-10">
          <div className="flex flex-col items-start space-y-4">
            <div className="text-center w-full font-semibold">Requested by</div>
            <div>
              <div className="flex flex-col">
                <label className="mb-1">Sign</label>
                <div className="border-t border-black w-48"></div>
              </div>
              <div className="mt-4 flex flex-col">
                <label className="mb-1">
                  Name : {goodsRequestData?.data[0]?.first_name || ""}
                </label>
                <div className="border-t border-black w-48"></div>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-start space-y-4">
            <div className="text-center w-full font-semibold">Checked by</div>
            <div>
              <div className="flex flex-col">
                <label className="mb-1">Sign</label>
                <div className="border-t border-black w-48"></div>
              </div>
              <div className="mt-4 flex flex-col">
                <label className="mb-1">Name :</label>
                <div className="border-t border-black w-48"></div>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-start space-y-4">
            <div className="text-center w-full font-semibold">Approved by</div>
            <div>
              <div className="flex flex-col">
                <label className="mb-1">Sign</label>
                <div className="border-t border-black w-48"></div>
              </div>
              <div className="mt-4 flex flex-col">
                <label className="mb-1">Name :</label>
                <div className="border-t border-black w-48"></div>
              </div>
            </div>
          </div>
        </div>

        {/* <div className="col-span-3">
          <p className="text-left italic">{PRINT_TEXT.receivedCondition}</p>
        </div> */}

        {/* <div className="signatures grid grid-cols-3 gap-4 mt-6">
          <div className="signature-col text-center">
            <p className="font-semibold mb-2">Sender</p>
            <div className="border-b border-black mb-2"></div>
            <div className="signature-line relative h-8 mb-4">
              <span className="absolute left-0">Sign :</span>
              <div className="border-dotted border-b-2 border-black ml-12 h-6"></div>
            </div>
            <div className="signature-line relative h-8 mb-4">
              <span className="absolute left-0">Name :</span>
              <div className="border-dotted border-b-2 border-black ml-12 h-6"></div>
            </div>
            <div className="signature-line relative h-8">
              <span className="absolute left-0">Position :</span>
              <div className="border-dotted border-b-2 border-black ml-12 h-6"></div>
            </div>
          </div>

          <div className="signature-col text-center">
            <p className="font-semibold mb-2">Inspector</p>
            <div className="border-b border-black flex-1 mb-2"></div>
            <div className="signature-line relative h-8 mb-4">
              <span className="absolute left-0">Sign :</span>
              <div className="border-dotted border-b-2 border-black ml-12 h-6"></div>
            </div>
            <div className="signature-line relative h-8 mb-4">
              <span className="absolute left-0">Name :</span>
              <div className="border-dotted border-b-2 border-black ml-12 h-6"></div>
            </div>
            <div className="signature-line relative h-8">
              <span className="absolute left-0">Position :</span>
              <div className="border-dotted border-b-2 border-black ml-12 h-6"></div>
            </div>
          </div>

          <div className="signature-col text-center">
            <p className="font-semibold mb-2">Receiver</p>
            <div className="border-b border-black mb-2 w-30"></div>
            <div className="signature-line relative h-8 mb-4">
              <span className="absolute left-0">Sign :</span>
              <div className="border-dotted border-b-2 border-black ml-12 h-6"></div>
            </div>
            <div className="signature-line relative h-8 mb-4">
              <span className="absolute left-0">Name :</span>
              <div className="border-dotted border-b-2 border-black ml-12 h-6"></div>
            </div>
            <div className="signature-line relative h-8">
              <span className="absolute left-0">Position :</span>
              <div className="border-dotted border-b-2 border-black ml-12 h-6"></div>
            </div>
          </div>

          <div className="col-span-3 text-left mt-4">
            <p className="text-sm absolute left-0">FORM: 0/1 READ</p>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default GoodsRequestNotePrintPage;
